<script lang="ts">
  import { untrack } from 'svelte';
  import { api } from '../../api/client';
  import type { ArriveByAlert, Member, Place } from '../../api/types';
  import { currentUser } from '../../stores/auth';
  import { confirm } from '../../stores/confirmDialog';
  import PageHeader from '../PageHeader.svelte';
  import { arriveByDays, arriveByTime, arriveByZone } from '../../utils/arriveby';
  import { t, weekday } from '../../i18n/index.ts';

  // ArriveBy is a group's arrive-by alerts on its page (spec 003): the
  // viewer's own, each with Edit and Delete, a form to add one, and the
  // alerts others set on the viewer, which the viewer only sees (003 FR-043).
  // The page hands over the group's members, which it keeps current
  // (001 FR-112).
  let { groupId, members }: { groupId: string; members: Member[] } = $props();

  // DAYS are the short weekday names, 0 Sunday; January 1, 2023 was a Sunday.
  const DAYS = Array.from({ length: 7 }, (_, d) => weekday(new Date(2023, 0, 1 + d), true));

  // MEMBER, PLACE, and TIME stand in the form's sentence for its controls,
  // so a translation may order them as its language needs.
  const MEMBER = '\u0001';
  const PLACE = '\u0002';
  const TIME = '\u0003';
  const sentence = t('alertsArriveByForm', { member: MEMBER, place: PLACE, time: TIME }).split(/(\u0001|\u0002|\u0003)/).map((s) => s.trim()).filter((s) => s);

  let alerts: ArriveByAlert[] = $state([]);
  let places: Place[] = $state([]);
  let error = $state('');
  // form is the alert being added or edited; id is empty for a new one.
  let form: { id: string; member: string; place: string; time: string; once: boolean; date: string; days: number[]; tz?: string } | null = $state(null);

  const mine = $derived(alerts.filter((a) => a.setter_id === $currentUser?.id));
  const onMe = $derived(alerts.filter((a) => a.member_id === $currentUser?.id));
  const others = $derived(members.filter((m) => m.user_id !== $currentUser?.id));

  $effect(() => {
    const id = groupId;
    form = null;
    // A reply for a group no longer shown is dropped, so a slow one cannot
    // fill in another group's places.
    api.get<Place[]>(`/groups/${id}/places`)
      .then((p) => { if (id === groupId) places = p; })
      .catch((e: any) => { if (id === groupId) error = e.message; });
  });

  // The alerts are read again whenever the page's members change, since a
  // member who leaves the group takes the alerts about them along.
  $effect(() => {
    const id = groupId;
    members;
    untrack(() => load(id).catch((e: any) => { if (id === groupId) error = e.message; }));
  });

  async function load(id = groupId) {
    const list = await api.get<ArriveByAlert[]>(`/groups/${id}/arrive-by`);
    if (id !== groupId) return;
    alerts = list;
    error = '';
  }

  function today(): string {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  function add() {
    form = { id: '', member: others[0]?.user_id ?? '', place: places[0]?.id ?? '', time: '08:00', once: false, date: today(), days: [1, 2, 3, 4, 5] };
  }

  function edit(a: ArriveByAlert) {
    form = { id: a.id, member: a.member_id, place: a.place_id, time: a.time, once: !!a.date, date: a.date ?? today(), days: [...a.weekdays], tz: a.tz };
  }

  function toggleDay(d: number) {
    if (!form) return;
    form.days = form.days.includes(d) ? form.days.filter((x) => x !== d) : [...form.days, d].sort();
  }

  async function save() {
    if (!form) return;
    const body = {
      member_id: form.member,
      place_id: form.place,
      time: form.time,
      // An edited alert keeps its zone, so editing while away does not move it.
      tz: form.tz ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
      ...(form.once ? { date: form.date } : { weekdays: form.days }),
    };
    try {
      if (form.id) await api.put(`/groups/${groupId}/arrive-by/${form.id}`, body);
      else await api.post(`/groups/${groupId}/arrive-by`, body);
      form = null;
      await load();
    } catch (e: any) {
      error = e.message;
    }
  }

  async function remove(a: ArriveByAlert) {
    if (!(await confirm(t('alertsArriveByDeleteConfirm', { member: a.member_name, place: a.place_name })))) return;
    try {
      await api.delete(`/groups/${groupId}/arrive-by/${a.id}`);
      await load();
    } catch (e: any) {
      error = e.message;
    }
  }
</script>

<div class="card p-5 mb-6 space-y-3">
  <PageHeader title={t('alertsArriveByTitle')} level="section">
    {#if !form && others.length > 0 && places.length > 0}
      <button class="btn btn-secondary btn-sm" onclick={add}><i aria-hidden="true" class="las la-plus mr-1"></i>{t('appAdd')}</button>
    {/if}
  </PageHeader>
  <p class="text-sm text-text-muted">{t('alertsArriveByIntro')}</p>
  {#if places.length === 0}
    <p class="text-sm text-text-muted">{t('alertsArriveByNoPlaces')}</p>
  {/if}
  {#if error}<p class="text-sm" style="color: var(--theme-error);">{error}</p>{/if}

  {#if form}
    <div class="border rounded p-3 space-y-3 text-sm" style="border-color: var(--theme-border);">
      <div class="flex flex-wrap items-center gap-2">
        {#each sentence as part, k (k)}
          {#if part === MEMBER}
            <select class="input py-1" bind:value={form.member} aria-label={t('alertsMember')}>
              {#each others as m (m.user_id)}<option value={m.user_id}>{m.display_name}</option>{/each}
            </select>
          {:else if part === PLACE}
            <select class="input py-1" bind:value={form.place} aria-label={t('alertsPlace')}>
              {#each places as p (p.id)}<option value={p.id}>{p.name}</option>{/each}
            </select>
          {:else if part === TIME}
            <input class="input py-1 w-32" type="time" bind:value={form.time} aria-label={t('alertsTime')} />
          {:else}
            <span>{part}</span>
          {/if}
        {/each}
      </div>
      <div class="flex flex-wrap items-center gap-3">
        <label class="flex items-center gap-1"><input type="radio" bind:group={form.once} value={false} />{t('alertsEveryWeekOn')}</label>
        {#if !form.once}
          <div class="flex gap-1">
            {#each DAYS as name, d (d)}
              <button
                class="btn btn-sm {form.days.includes(d) ? 'btn-primary' : 'btn-secondary'}"
                aria-pressed={form.days.includes(d)}
                onclick={() => toggleDay(d)}>{name}</button>
            {/each}
          </div>
        {/if}
      </div>
      <div class="flex flex-wrap items-center gap-3">
        <label class="flex items-center gap-1"><input type="radio" bind:group={form.once} value={true} />{t('alertsOnceOn')}</label>
        {#if form.once}<input class="input py-1" type="date" bind:value={form.date} aria-label={t('alertsDate')} />{/if}
      </div>
      <div class="flex gap-2">
        <button class="btn btn-primary btn-sm" disabled={!form.once && form.days.length === 0} onclick={save}>{t('appSave')}</button>
        <button class="btn btn-secondary btn-sm" onclick={() => (form = null)}>{t('appCancel')}</button>
      </div>
    </div>
  {/if}

  {#each mine as a (a.id)}
    <div class="flex justify-between gap-2 text-sm border rounded p-3" style="border-color: var(--theme-border);">
      <div>
        <p><i aria-hidden="true" class="las la-user-clock mr-1"></i>{t('alertsArriveByLine', { member: a.member_name, place: a.place_name, time: `${arriveByTime(a.time)}${arriveByZone(a.tz)}` })}</p>
        <p class="text-text-muted">{a.last_result === 'missing' ? t('alertsArriveByDaysMissed', { days: arriveByDays(a) }) : arriveByDays(a)}</p>
      </div>
      <div class="flex gap-2 shrink-0 items-start">
        <button class="btn btn-secondary btn-sm" onclick={() => edit(a)}>{t('appEdit')}</button>
        <button class="btn btn-secondary btn-sm" onclick={() => remove(a)}>{t('appDelete')}</button>
      </div>
    </div>
  {/each}

  {#if onMe.length > 0}
    <p class="text-sm font-semibold pt-1">{t('alertsArriveByOnYou')}</p>
    {#each onMe as a (a.id)}
      <p class="text-sm"><i aria-hidden="true" class="las la-user-clock mr-1"></i>{t('alertsArriveByExpectsYou', {
        setter: a.setter_name, place: a.place_name, time: `${arriveByTime(a.time)}${arriveByZone(a.tz)}`, days: arriveByDays(a).toLowerCase(),
      })}</p>
    {/each}
  {/if}
</div>
